import React, { useEffect, useState } from 'react';
import { GetById_User_Api, Update_User_Api } from '../../API/userApi';
import { 
    Button, 
    Checkbox, 
    Col, 
    Form, 
    Input, 
    Row, 
    Upload, 
    Card,
    Avatar,
    Space,
    Tooltip,
    Divider
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { 
    RollbackOutlined, 
    CheckOutlined, 
    UploadOutlined, 
    UserOutlined,
    EyeOutlined,
    EditOutlined,
    QuestionCircleOutlined,
    IdcardOutlined,
    HomeOutlined
} from '@ant-design/icons';
import { CustomTypeCombo } from '../CustomType/CustomTypeCombo';

// -----------------------------------------------------------
// وارد کردن DatePicker و تنظیمات شمسی
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
// -----------------------------------------------------------

// =================================================================
const SERVER_DATE_FORMAT = "YYYY-MM-DD"; 

const persianToEnglishNumbers = (str) => {
    if (!str) return '';
    let output = String(str).trim(); 
    const persianNumbers = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
    const arabicNumbers = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
    const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    for (let i = 0; i < 10; i++) {
        output = output.replace(persianNumbers[i], englishNumbers[i]);
        output = output.replace(arabicNumbers[i], englishNumbers[i]);
    }
    return output.replace(/\//g, "-"); 
};

// =================================================================
const PersianDatePickerAdapter = ({ value, onChange }) => {
    const [dateValue, setDateValue] = useState(null);

    const toDateObject = (dateString) => {
        if (!dateString) return null;
        const englishDateString = persianToEnglishNumbers(dateString).replace(/\//g, "-"); 
        return new DateObject({
            calendar: persian,
            locale: persian_fa,
            date: englishDateString,
            format: SERVER_DATE_FORMAT 
        });
    };
    
    useEffect(() => {
        setDateValue(toDateObject(value));
    }, [value]);

    const handleOnChange = (dateObject) => {
        setDateValue(dateObject); 
        let dateString = null;
        if (dateObject && !Array.isArray(dateObject)) {
            dateString = persianToEnglishNumbers(dateObject.format(SERVER_DATE_FORMAT));
        }
        onChange(dateString);
    };

    return (
        <DatePicker
            calendar={persian}
            locale={persian_fa}
            value={dateValue}
            onChange={handleOnChange}
            inputClass="ant-input persian-date-input" 
            calendarPosition="bottom-right"
            format={SERVER_DATE_FORMAT} 
            placeholder="تاریخ تولد را انتخاب کنید"
            style={{ width: '100%' }}
        />
    );
};

// =================================================================
// اعتبارسنجی‌ها
const validateNationalCode = (_, value) => {
    if (!value) return Promise.reject(new Error('کد ملی الزامی است!'));
    if (!/^\d{10}$/.test(value)) return Promise.reject(new Error('کد ملی باید دقیقاً ۱۰ رقم باشد!'));
    const checkNationalCode = (code) => {
        if (code.length !== 10 || /(\d)(\1){9}/.test(code)) return false;
        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(code[i]) * (10 - i);
        }
        let remainder = sum % 11;
        let controlDigit = remainder < 2 ? remainder : 11 - remainder;
        return controlDigit === parseInt(code[9]);
    };
    if (!checkNationalCode(value)) return Promise.reject(new Error('کد ملی معتبر نیست!'));
    return Promise.resolve();
};

const validatePostalCode = (_, value) => {
    if (!value) return Promise.reject(new Error('کد پستی الزامی است!'));
    if (!/^\d{10}$/.test(value)) return Promise.reject(new Error('کد پستی باید دقیقاً ۱۰ رقم باشد!'));
    return Promise.resolve();
};

const validateAddress = (_, value) => {
    if (!value) return Promise.reject(new Error('آدرس الزامی است!'));
    if (value.trim().length < 8) return Promise.reject(new Error('آدرس باید حداقل ۸ کاراکتر باشد!'));
    return Promise.resolve();
};

// کامپوننت Label با آیکون راهنما
const LabelWithHelp = ({ label, helpText }) => (
    <Space size="small">
        <span>{label}</span>
        <Tooltip title={helpText}>
            <QuestionCircleOutlined style={{ color: '#1890ff', fontSize: '12px', cursor: 'pointer' }} />
        </Tooltip>
    </Space>
);

export const EditUserComponent = ({ id, setLoading, showMessage, onRowUpdated, cancelEdit }) => {
    const [user, setUser] = useState();
    const [form] = Form.useForm();
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [stateId, setStateId] = useState(null);
    const [cityId, setCityId] = useState(null);
    const [uploadFileList, setUploadFileList] = useState([]);
    const [isVerifiedMobile, setIsVerifiedMobile] = useState(false);

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await GetById_User_Api(id);
            setLoading(false);
            if (data.messageStatus === "Successful") {
                const userData = data.messageData.data;
                setUser(userData);
                
                // مقداردهی اولیه وضعیت تأیید
                setIsVerifiedMobile(userData.isVerifiedMobile || false);
                
                form.setFieldsValue({
                    ...userData,
                    shamsiBirthDate: userData.shamsiBirthDate || userData.birthDate
                });
                if (userData.photoUrl) {
                    setFilePreview(userData.photoUrl);
                }
                if (userData.birthPlaceId) {
                    setCityId(userData.birthPlaceId);
                }
            } else {
                showMessage(data.message, "error");
            }
        } catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور.", "error");
        }
    };

    // تابع مدیریت تغییر چک‌باکس
    const handleVerifiedMobileChange = (e) => {
        const checked = e.target.checked;
        setIsVerifiedMobile(checked);
        
        // اگر چک‌باکس انتخاب شد و موبایل یا تلفن ثابت خالی است، خطا نشان دهید
        if (checked) {
            const mobileValue = form.getFieldValue('mobile');
            const telValue = form.getFieldValue('tel');
            
            if (!mobileValue) {
                form.validateFields(['mobile']).catch(() => {});
            }
            if (!telValue) {
                form.validateFields(['tel']).catch(() => {});
            }
        }
    };

    // تابع اعتبارسنجی پویا برای موبایل
    const mobileValidator = (_, value) => {
        if (isVerifiedMobile && !value) {
            return Promise.reject(new Error('برای کاربر تأیید شده، شماره موبایل الزامی است!'));
        }
        if (value && !/^09\d{9}$/.test(value)) {
            return Promise.reject(new Error('فرمت موبایل معتبر نیست'));
        }
        return Promise.resolve();
    };

    // تابع اعتبارسنجی پویا برای تلفن ثابت
    const telValidator = (_, value) => {
        if (isVerifiedMobile && !value) {
            return Promise.reject(new Error('برای کاربر تأیید شده، تلفن ثابت الزامی است!'));
        }
        if (value && !/^0\d{2,3}\d{7,8}$/.test(value)) {
            return Promise.reject(new Error('فرمت تلفن ثابت معتبر نیست'));
        }
        return Promise.resolve();
    };

    const onSubmit = async (values) => {
        setLoading(true);
        try {
            values.id = id;
            values.birthPlaceId = cityId || user?.birthPlaceId;

            const formData = new FormData();
            
            // ارسال عکس جدید اگر وجود دارد
            if (file) {
                formData.append("photo", file);
            }
            
            // ارسال عکس قبلی به سرور برای اطلاع از وجود عکس قدیمی
            if (user?.photoUrl && !file) {
                formData.append("previousPhotoUrl", user.photoUrl);
            }

            Object.entries(values).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formData.append(key, value.toString());
                }
            });

            let data = await Update_User_Api(formData);
            setLoading(false);

            if (data.messageStatus === "Successful") {
                showMessage("اطلاعات کاربر با موفقیت به‌روزرسانی شد", "success");
                if (onRowUpdated) onRowUpdated();
            } else {
                showMessage(data.message, "error");
            }
        } catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور.", "error");
        }
    };

    const onFinishFailed = (errorInfo) => {
        const errorFields = errorInfo.errorFields;
        let errorMessage = "خطا در تکمیل فرم:\n";
        if (errorFields && errorFields.length > 0) {
            errorFields.forEach(field => {
                const fieldName = field.name.join(' '); 
                const fieldErrors = field.errors.join(' | '); 
                errorMessage += `\n${fieldName}: ${fieldErrors}`;
            });
        }
        showMessage(errorMessage, "error");
    };
    
    const handleFileChange = (info) => {
        if (info.fileList.length > 0) {
            const selectedFile = info.fileList[0].originFileObj;
            if (selectedFile.size > 2 * 1024 * 1024) {
                showMessage("حجم فایل باید کمتر از ۲MB باشد", "error");
                return;
            }
            setFile(selectedFile);
            setUploadFileList(info.fileList);
            
            const reader = new FileReader();
            reader.onload = (e) => setFilePreview(e.target.result);
            reader.readAsDataURL(selectedFile);
        } else {
            setFile(null);
            setUploadFileList([]);
            setFilePreview(user?.photoUrl || null);
        }
    };

    return (
        <>
            {user ? (
                <div style={{ padding: '0', minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
                    <Card 
                        style={{ 
                            borderRadius: '0', 
                            boxShadow: 'none', 
                            minHeight: '100vh', 
                            margin: 0, 
                            padding: 0 
                        }}
                        bodyStyle={{ 
                            padding: '16px', 
                            margin: 0,
                            maxWidth: '100%'
                        }}
                    >
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '12px 0', 
                            marginBottom: '16px',
                            borderBottom: '1px solid #e8e8e8'
                        }}>
                            <Space>
                                <EditOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                                <span style={{ fontWeight: 'bold', fontSize: '18px' }}>ویرایش کاربر</span>
                            </Space>
                        </div>

                        <Form
                            form={form}
                            name="editUserForm"
                            layout="horizontal"
                            onFinish={onSubmit}
                            onFinishFailed={onFinishFailed}
                            size="large"
                            labelCol={{ xs: 24, sm: 8, md: 6 }}
                            wrapperCol={{ xs: 24, sm: 16, md: 18 }}
                            requiredMark={false}
                        >
                            <Form.Item name="id" hidden={true} />

                            {/* اطلاعات شخصی */}
                            <Divider orientation="left" orientationMargin="0" style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '24px' }}>
                                <Space>
                                    <IdcardOutlined style={{ color: '#52c41a' }} />
                                    اطلاعات شخصی
                                </Space>
                            </Divider>

                            <Form.Item
                                label={<LabelWithHelp label="نام" helpText="نام کاربر را به فارسی وارد کنید" />}
                                name="name"
                                rules={[{ required: true, message: 'لطفاً نام را وارد کنید!' }]}
                            >
                                <Input placeholder="نام" />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="نام خانوادگی" helpText="نام خانوادگی کاربر را به فارسی وارد کنید" />}
                                name="family"
                                rules={[{ required: true, message: 'لطفاً نام خانوادگی را وارد کنید!' }]}
                            >
                                <Input placeholder="نام خانوادگی" />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="نام پدر" helpText="نام پدر کاربر را به فارسی وارد کنید" />}
                                name="fatherName"
                                rules={[{ required: true, message: 'لطفاً نام پدر را وارد کنید!' }]}
                            >
                                <Input placeholder="نام پدر" />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="کد ملی" helpText="کد ملی باید ۱۰ رقم و معتبر باشد" />}
                                name="nationalCode"
                                rules={[{ validator: validateNationalCode }]}
                            >
                                <Input 
                                    placeholder="کد ملی" 
                                    maxLength={10}
                                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                    style={{ textAlign: 'left', direction: 'ltr' }}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="تاریخ تولد" helpText="تاریخ تولد کاربر را به شمسی انتخاب کنید" />}
                                name="shamsiBirthDate"
                                rules={[{ required: true, message: 'تاریخ تولد الزامی است!' }]}
                            >
                                <PersianDatePickerAdapter />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="شماره موبایل" helpText="شماره موبایل باید با ۰۹ شروع شود" />}
                                name="mobile"
                                rules={[{ validator: mobileValidator }]}
                            >
                                <Input 
                                    placeholder="موبایل" 
                                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                    style={{ textAlign: 'left', direction: 'ltr' }}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="تلفن ثابت" helpText="شماره تلفن ثابت با پیش‌شهر" />}
                                name="tel"
                                rules={[{ validator: telValidator }]}
                            >
                                <Input 
                                    placeholder="تلفن ثابت" 
                                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                    style={{ textAlign: 'left', direction: 'ltr' }}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="کد پستی" helpText="کد پستی باید ۱۰ رقم باشد" />}
                                name="postalCode"
                                rules={[{ validator: validatePostalCode }]}
                            >
                                <Input 
                                    placeholder="کد پستی" 
                                    maxLength={10}
                                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                    style={{ textAlign: 'left', direction: 'ltr' }}
                                />
                            </Form.Item>
                            <Form.Item
    label={<LabelWithHelp label="شماره شبا" helpText=" شماره شبا جهت برگشت وجه واریزی در صورت نیاز" />}
    name="shebaNumber"
    rules={[
        {
            pattern: /^IR\d{24}$/,
            message: "شماره شبا باید با IR شروع شده و 24 رقم داشته باشد"
        }
    ]}
>
    <Input
        placeholder="IRxxxxxxxxxxxxxxxxxxxxxxxx"
        maxLength={26}
        style={{ direction: "ltr", textAlign: "left" }}
    />
</Form.Item>

                            {/* اطلاعات آدرس */}
                            <Divider orientation="left" orientationMargin="0" style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '24px' }}>
                                <Space>
                                    <HomeOutlined style={{ color: '#fa8c16' }} />
                                    اطلاعات آدرس
                                </Space>
                            </Divider>

                            <Form.Item
                                label={<LabelWithHelp label="محل تولد فعلی" helpText="محل تولد ثبت شده کاربر" />}
                            >
                                <Input 
                                    value={user.birthPlaceTitle} 
                                    readOnly 
                                    style={{ background: '#f5f5f5' }}
                                />
                            </Form.Item>

                            {/* تغییر محل تولد در صورت نیاز */}
                            <Form.Item
                                label={<LabelWithHelp label="تغییر محل تولد" helpText="در صورت نیاز می‌توانید استان و شهر محل تولد را تغییر دهید" />}
                            >
                                <Row gutter={16} style={{ marginTop: '8px' }}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label={<LabelWithHelp label="استان" helpText="استان محل تولد را انتخاب کنید" />}
                                            labelCol={{ span: 24 }}
                                            wrapperCol={{ span: 24 }}
                                            style={{ marginBottom: '16px' }}
                                        >
                                            <CustomTypeCombo
                                                setLoading={setLoading}
                                                showMessage={showMessage}
                                                placeHolder="انتخاب استان"
                                                parentId={2}
                                                onChange={setStateId}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            label={<LabelWithHelp label="شهر" helpText="شهر محل تولد را انتخاب کنید" />}
                                            labelCol={{ span: 24 }}
                                            wrapperCol={{ span: 24 }}
                                            style={{ marginBottom: '16px' }}
                                        >
                                            <CustomTypeCombo
                                                setLoading={setLoading}
                                                showMessage={showMessage}
                                                placeHolder="انتخاب شهر"
                                                parentId={stateId}
                                                onChange={setCityId}
                                                disabled={!stateId}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="آدرس کامل" helpText="آدرس کامل با حداقل ۸ کاراکتر" />}
                                name="address"
                                rules={[{ validator: validateAddress }]}
                            >
                                <TextArea 
                                    placeholder="آدرس کامل"
                                    autoSize={{ minRows: 2, maxRows: 4 }}
                                />
                            </Form.Item>

                            {/* تصویر پروفایل */}
                            <Divider orientation="left" orientationMargin="0" style={{ fontWeight: 'bold', fontSize: '16px', marginTop: '24px' }}>
                                <Space>
                                    <UserOutlined />
                                    تصویر پروفایل
                                </Space>
                            </Divider>

                            <Form.Item
                                label={<LabelWithHelp label="عکس پروفایل" helpText="فرمت‌های مجاز: JPG, PNG, GIF - حداکثر سایز: ۲MB" />}
                            >
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '12px',
                                    flexWrap: 'wrap'
                                }}>
                                    <Avatar
                                        size={60}
                                        src={filePreview}
                                        icon={!filePreview && <UserOutlined />}
                                        style={{ border: '1px solid #d9d9d9' }}
                                    />
                                    <Space direction="vertical" size="small">
                                        <Upload
                                            beforeUpload={() => false}
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            maxCount={1}
                                            showUploadList={false}
                                            fileList={uploadFileList}
                                        >
                                            <Button icon={<UploadOutlined />} size="small">
                                                تغییر تصویر
                                            </Button>
                                        </Upload>
                                        {filePreview && (
                                            <Button 
                                                icon={<EyeOutlined />} 
                                                size="small"
                                                onClick={() => window.open(filePreview)}
                                                style={{ width: '100%' }}
                                            >
                                                مشاهده
                                            </Button>
                                        )}
                                    </Space>
                                </div>
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="وضعیت" helpText="کاربر تایید شده با موبایل و تلفن ثابت" />}
                                name="isVerifiedMobile"
                                valuePropName="checked"
                            >
                                <Checkbox onChange={handleVerifiedMobileChange}>کاربر تأیید شده</Checkbox>
                            </Form.Item>
                            {(localStorage.getItem('role') === 'SuperAdmin' || localStorage.getItem('role') === 'Admin') && (
<Form.Item
    label="لیست سیاه"
    name="isBlackList"
    valuePropName="checked"
>
    <Checkbox>
        کاربر در لیست سیاه است
    </Checkbox>
</Form.Item>
)}
{(localStorage.getItem('role') === 'SuperAdmin' || localStorage.getItem('role') === 'Admin') && (
<Form.Item
    label="توضیحات لیست سیاه"
    name="blackListDescription"
>
    <TextArea
        rows={4}
        maxLength={2000}
        showCount
    />
</Form.Item>
)}

                            {/* دکمه‌های اقدام */}
                            <Row className='sectionFooter'>
                                <Col xs={0} sm={0} md={8} lg={16} xl={16} />
                                <Col xs={12} sm={12} md={8} lg={4} xl={4} >
                                    <Button
                                        style={{ width: '98%' }}
                                        className='btn-primary'
                                        htmlType="submit"
                                        onClick={() => form.submit()}>
                                        <CheckOutlined />
                                        ذخیره
                                    </Button>
                                </Col>
                                <Col xs={12} sm={12} md={8} lg={4} xl={4} >
                                    <Button
                                        className='btn-secondary'
                                        style={{ width: '100%' }}
                                        onClick={() => cancelEdit()}
                                    >
                                        <RollbackOutlined />
                                        لغو
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <p>در حال بارگذاری اطلاعات کاربر...</p>
                </div>
            )}
        </>
    );
};







/*
import React, { useEffect, useState } from 'react';
import { GetById_User_Api, Update_User_Api } from '../../API/userApi';
import { Button, Checkbox, Col, Form, Input, Row, Upload } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { RollbackOutlined, CheckOutlined, UploadOutlined } from '@ant-design/icons';
import { CustomTypeCombo } from '../CustomType/CustomTypeCombo';

// -----------------------------------------------------------
// وارد کردن DatePicker و تنظیمات شمسی (جایگزین antd-jalali و dayjs)
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
// -----------------------------------------------------------

// =================================================================
const SERVER_DATE_FORMAT = "YYYY-MM-DD"; 

/**
 * تابع کمکی برای تبدیل ارقام فارسی/عربی به انگلیسی و تبدیل اسلش به خط تیره.
 */
/*const persianToEnglishNumbers = (str) => {
    if (!str) return '';
    let output = String(str).trim(); 

    const persianNumbers = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
    const arabicNumbers = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
    const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

    for (let i = 0; i < 10; i++) {
        output = output.replace(persianNumbers[i], englishNumbers[i]);
        output = output.replace(arabicNumbers[i], englishNumbers[i]);
    }

    // جایگزینی اسلش با خط تیره
    return output.replace(/\//g, "-"); 
};

// =================================================================
/**
 * آداپتور سفارشی PersianDatePickerAdapter
 */
/*const PersianDatePickerAdapter = ({ value, onChange }) => {
    const [dateValue, setDateValue] = useState(null);

    const toDateObject = (dateString) => {
        if (!dateString) return null;
        const englishDateString = persianToEnglishNumbers(dateString).replace(/\//g, "-"); 
        
        return new DateObject({
            calendar: persian,
            locale: persian_fa,
            date: englishDateString,
            format: SERVER_DATE_FORMAT 
        });
    };
    
    useEffect(() => {
        setDateValue(toDateObject(value));
    }, [value]);

    const handleOnChange = (dateObject) => {
        setDateValue(dateObject); 

        let dateString = null;
        if (dateObject && !Array.isArray(dateObject)) {
            // اطمینان از خروجی استاندارد YYYY-MM-DD با ارقام انگلیسی و خط تیره
            dateString = persianToEnglishNumbers(dateObject.format(SERVER_DATE_FORMAT));
        }
        
        // ارسال مقدار رشته‌ای تمیز و انگلیسی (با خط تیره) به Form.Item
        onChange(dateString);
    };

    return (
        <DatePicker
            calendar={persian}
            locale={persian_fa}
            value={dateValue}
            onChange={handleOnChange}
            inputClass="ant-input persian-date-input" 
            calendarPosition="bottom-right"
            format={SERVER_DATE_FORMAT} 
            placeholder={"تاریخ تولد"}
        />
    );
};
// =================================================================


export const EditUserComponent = ({ id, setLoading, showMessage, onRowUpdated, cancelEdit }) => {
    // =================================================================
    const [user, setUser] = useState();
    const [form] = Form.useForm()
    const [file, setFile] = useState(null);
    const [stateId, setStateId] = useState(null);
    const [cityId, setCityId] = useState(null);
    // =================================================================
    useEffect(() => {
        loadData();
    }, [id]);
    // =================================================================
    const loadData = async () => {
        setLoading(true);
        try {
            const data = await GetById_User_Api(id);
            setLoading(false);
            if (data.messageStatus === "Successful") {
                const userData = data.messageData.data;
                setUser(userData);
                form.setFieldsValue(userData);
                // initBirthDate و stateهای مربوط به تاریخ شمسی حذف شدند
            }
            else
                showMessage(data.message, "error")
        }
        catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور.", "error")
        }
    }
    // =================================================================
    const onSubmit = async (values) => {
        setLoading(true);
        try {
            // نیازی به مقداردهی دستی shamsiBirthDate از state نیست
            // Form.Item با نام shamsiBirthDate مقدار را از PersianDatePickerAdapter دریافت کرده است.
            values.id = id;
            values.birthPlaceId = cityId;

            //////////////////////////////
            const formData = new FormData();
            if (file)
                formData.append("photo", file);

            // اضافه کردن مقادیر فرم به FormData
            Object.entries(values).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    // اطمینان از ارسال مقدار تاریخ شمسی به سرور
                    formData.append(key, value);
                }
            });
             // برای اطمینان، تاریخ تولد را با نام درست به سرور بفرستید
        // اگر سرور انتظار نام متفاوتی دارد:
      //  if (values.shamsiBirthDate) {
      //      formData.append("birthDate", values.shamsiBirthDate); // یا هر نامی که سرور انتظار دارد
      //  }
      // اگر cityId null است، از مقدار اصلی کاربر استفاده کنید
        if (!cityId && user?.birthPlaceId) {
            values.birthPlaceId = user.birthPlaceId;
        }

            let data = await Update_User_Api(formData);
            ///////////////////////////

            setLoading(false);

            if (data.messageStatus === "Successful") {
                showMessage(data.message, "Successful");
                if (onRowUpdated) onRowUpdated();
            }
            else
                showMessage(data.message, "error")
        }
        catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور.", "error")
        }
    };

    // =================================================================
    const onFinishFailed = (errorInfo) => {
        const errorFields = errorInfo.errorFields;
        let errorMessage = "خطا در تکمیل فرم. فیلدهای زیر مشکل دارند: \n";
        
        if (errorFields && errorFields.length > 0) {
            errorFields.forEach(field => {
                const fieldName = field.name.join(' '); 
                const fieldErrors = field.errors.join(' | '); 
                errorMessage += `\n ${fieldName}: ${fieldErrors}`;
            });
        } else {
             errorMessage = "خطا در تکمیل فرم: " + JSON.stringify(errorInfo);
        }

        showMessage(errorMessage, "error");
    };
    
    // =================================================================
    const handleFileChange = (info) => {
        // انتخاب اولین فایل
        if (info.fileList.length > 0) {
            setFile(info.fileList[0].originFileObj);
        } else {
            setFile(null);
        }
    };
    // =================================================================
    return (
        <>
            {user ?
                <>
                    <div className='sectionTitle'>
                        <h2 >
                            ویرایش کاربر
                        </h2>
                    </div>
                    <div className='container'>
                        <Form
                            form={form}
                            name="basic"
                            labelCol={{ span: 4 }}
                            wrapperCol={{ span: 20 }}
                            onFinish={onSubmit}
                            onFinishFailed={onFinishFailed}
                        >

                            <Form.Item
                                name="id"
                                hidden={true}
                            />
                            <Form.Item
                                name="name"
                                label="نام"
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="family"
                                label="نام خانوادگی"
                            >
                                <Input />

                            </Form.Item>

                            <Form.Item
                                name="nationalCode"
                                label="کد ملی"
                            >
                                <Input style={{ textAlign: 'left' }} />
                            </Form.Item>

                            <Form.Item
                                name="fatherName"
                                label="نام پدر"
                            >
                                <Input />
                            </Form.Item>

                            {/* نام فیلد 'tel' به 'shamsiBirthDate' تغییر یافت و آداپتور تاریخ شمسی استفاده شد *///}
                  /*          <Form.Item
                                name="shamsiBirthDate" 
                                label="تاریخ تولد"
                            >
                                <PersianDatePickerAdapter />
                            </Form.Item>

                            <Form.Item
                                name="mobile"
                                label="شماره موبایل"
                            >
                                <Input style={{ width: '100%', textAlign: 'left' }} />
                            </Form.Item>

                            <Form.Item
                                name="tel"
                                label="تلفن ثابت"
                            >
                                <Input style={{ textAlign: 'left' }} />
                            </Form.Item>


                            <Form.Item
                                name="postalCode"
                                label="کد پستی"
                            >
                                <Input style={{ textAlign: 'left' }} />
                            </Form.Item>

                            <Form.Item
                                name="birthPlaceTitle"
                                label="محل تولد"
                            >
                                <Input readOnly />
                            </Form.Item>
                            <Form.Item
                                label="تغییر محل تولد"
                            >
                                <Row justify={'space-between'}>
                                    <Col span={11}>
                                        <CustomTypeCombo
                                            setLoading={setLoading}
                                            showMessage={showMessage}
                                            placeHolder="استان تولد"
                                            parentId={2}
                                            onChange={(value) => { setStateId(value) }}
                                        />
                                    </Col>
                                    <Col span={11}>
                                        <CustomTypeCombo
                                            setLoading={setLoading}
                                            showMessage={showMessage}
                                            placeHolder="شهر تولد"
                                            parentId={stateId}
                                            onChange={(value) => { setCityId(value) }}
                                        />
                                    </Col>
                                </Row>
                            </Form.Item>
                            <Form.Item
                                name="address"
                                label="آدرس"
                            >
                                <TextArea placeholder="آدرس" className='full-width'
                                    autoSize={{ minRows: 2, maxRows: 6 }} />
                            </Form.Item>

                            <Form.Item
                                name="photoUrl"
                                label="عکس پروفایل"
                            >
                                <Row>
                                    <Col>
                                        {/* نمایش عکس فعلی *///}
                                 /*       {user.photoUrl && <img src={user.photoUrl} style={{ width: '100px', height: '100px', objectFit: 'cover' }} alt="Profile" />}
                                    </Col>
                                    <Col style={{ paddingTop: '40px', paddingRight: '10px' }}>
                                        <Upload
                                            style={{ width: '95%' }}
                                            beforeUpload={() => false}
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            maxCount={1}
                                            listType="picture"
                                            fileList={file ? [{ uid: '-1', name: file.name, status: 'done' }] : []}
                                        >

                                            <Button icon={<UploadOutlined />} style={{ width: '100%' }}>تغییر تصویر پروفایل</Button>
                                        </Upload>

                                    </Col>


                                </Row>
                            </Form.Item>


                            <Form.Item
                                name="isVerifiedMobile"
                                valuePropName='checked'
                                label="فعال"
                            >
                                {/* اطمینان حاصل می‌کنیم که چک‌باکس درست عمل کند *///}
           /*                     <Checkbox>تأیید موبایل</Checkbox>
                            </Form.Item>
                        </Form>
                    </div>

                    <Row className='sectionFooter'>
                        <Col xs={0} sm={0} md={8} lg={16} xl={16} />
                        <Col xs={12} sm={12} md={8} lg={4} xl={4} >
                            <Button
                                style={{ width: '98%' }}
                                className='btn-primary'
                                htmlType="submit"
                                onClick={() => form.submit()}>
                                <CheckOutlined />
                                ذخیره
                            </Button>
                        </Col>
                        <Col xs={12} sm={12} md={8} lg={4} xl={4} >
                            <Button
                                className='btn-secondary'
                                style={{ width: '100%' }}
                                onClick={() => cancelEdit()}
                            >
                                <RollbackOutlined />
                                لغو </Button>
                        </Col>
                    </Row>
                </> : <></>
            }
        </>
    )
}
    */
